import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import {
  createColor, createColorWithOpacity,
} from '~/_shared/components/colorPicker/colorPicker.helpers';
import { TriStateRange } from '~/_shared/constants/triStateRange.enum';
import { useTerritoryEdit } from '~/_shared/hooks/useTerritoryEdit';
import { MapTool } from '~/_shared/types/toolsAndFeatures/mapTools.types';
import { useIsDataExportAllowed } from '~/_shared/types/toolsAndFeatures/useIsDataExportAllowed';
import { ModalType } from '~/modal/modalType.enum';
import { useModal } from '~/modal/useModal.hook';
import { BoundaryItemComponent } from '~/sidebar/sidebarApps/mapTools/boundary/activeBoundary/boundaryItem/boundaryItem.component';
import { getBoundaryStyle } from '~/sidebar/sidebarApps/mapTools/boundary/boundaryItem.helpers';
import { useBoundaryBatchEditLocations } from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useBoundaryBatchEditLocations';
import { useBoundaryExportData } from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useBoundaryExportData';
import { useRemoveCustomBoundary } from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useRemoveCustomBoundary';
import { useZoomToBoundary } from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useZoomToBoundary';
import type { ExtendedBoundaryIdentifier } from '~/store/boundaries/boundaryIdentifier.type';
import { type BoundaryGroupPermissions } from '~/store/boundaryGroups/boundaryGroups.selectors';
import type { BoundaryStateItem } from '~/store/boundaryItems/boundaryItems.state';
import { useBoundaryTerritoryDetailsSelector } from '~/store/boundaryTerritoryDetails/boundaryTerritoryDetails.selectors';
import type { BoundaryTerritoryGroup } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import { useIsConnectedLayeredMapSelector } from '~/store/mapInfo/mapInfo.selectors';
import { setBoundaryFilter } from '~/store/mapSettings/toolsState/boundary/mapSettingsToolsStateBoundary.actionCreators';
import { useBoundaryFilters } from '~/store/mapSettings/toolsState/boundary/mapSettingsToolsStateBoundary.selectors';
import {
  useIsMapEditDisabled, useIsMapPresentationalSelector,
} from '~/store/selectors/useMapInfoSelectors';

type BoundaryItemContainerProps = {
  readonly legendMode?: boolean;
  readonly boundary: BoundaryStateItem;
  readonly boundaryTerritoryGroup: BoundaryTerritoryGroup;
  readonly boundaryPermissions: BoundaryGroupPermissions;
};

export const BoundaryItemContainer = ({
  legendMode,
  boundary,
  boundaryTerritoryGroup,
  boundaryPermissions,
}: BoundaryItemContainerProps) => {

  const dispatch = useDispatch();
  const { openModal: openBoundaryItemSettingsModal } = useModal(ModalType.BoundaryItemSettings);
  const { openModal: openExportContainedBoundaries } = useModal(ModalType.ExportContainedBoundaryData);
  const { zoomToBoundary } = useZoomToBoundary();
  const boundaryFilters = useBoundaryFilters();
  const filter = boundaryFilters.get(boundaryTerritoryGroup.boundaryGroupId);
  const isMapPresentational = useIsMapPresentationalSelector();
  const isEditModeDisabled = useIsMapEditDisabled();
  const boundaryTerritoryDetails = useBoundaryTerritoryDetailsSelector();
  const isLayeredMapConnected = useIsConnectedLayeredMapSelector();
  const { batchEditLocationsInBoundary } = useBoundaryBatchEditLocations();
  const isBoundaryExportLocationsAllowed = useIsDataExportAllowed(MapTool.Boundary);
  const { removeCustomBoundary } = useRemoveCustomBoundary();
  const { exportBoundaryData } = useBoundaryExportData();
  const startTerritoryEdit = useTerritoryEdit(boundaryTerritoryGroup.boundaryTerritoryGroupId);
  const { boundaryIsEditable } = boundaryPermissions;

  const canEditBoundary = !isEditModeDisabled && boundaryIsEditable;
  const canRemoveBoundary = !isEditModeDisabled && boundaryIsEditable;

  const boundaryStyle = getBoundaryStyle(boundaryTerritoryGroup, boundary.id);
  const boundaryGroupMarkerCounts = boundaryTerritoryDetails.get(boundaryTerritoryGroup.boundaryTerritoryGroupId)?.counts;
  const boundaryMarkerCounts = boundaryGroupMarkerCounts?.[boundary.id] ?? 0;
  const isEmpty = boundaryMarkerCounts === 0;

  const setFilteredBoundaries = (filteredBoundaries: number[], showAll: TriStateRange) => {
    dispatch(setBoundaryFilter(boundaryTerritoryGroup.boundaryGroupId, {
      ...filter,
      filteredBoundaries,
      showAll,
    }));
  };

  const editBoundarySettings = useCallback((identifier: ExtendedBoundaryIdentifier) => {
    openBoundaryItemSettingsModal(identifier);
  }, [openBoundaryItemSettingsModal]);

  const setting = {
    isVisible: filter.filteredBoundaries.includes(boundary.id),
    label: boundary.displayName,
    selectedColor: createColorWithOpacity(boundaryStyle.color, boundaryStyle.opacity / 100),
    lineColor: createColor(boundaryTerritoryGroup.settings.style.lineColor),
    lineWidth: boundaryTerritoryGroup.settings.style.lineWidth,
    onToggle: () => {
      const newFilter = filter.filteredBoundaries
        .filter(boundaryId => boundaryId !== boundary.id);

      if (newFilter.length === filter.filteredBoundaries.length) {
        newFilter.push(boundary.id);
      }

      const showAll = newFilter.length ? TriStateRange.Partial : TriStateRange.None;

      setFilteredBoundaries(newFilter, showAll);
    },
    isCustom: true,
    onBatchEditLocationsClick: !isEditModeDisabled && !isLayeredMapConnected && !isEmpty ? () => {
      batchEditLocationsInBoundary(boundaryTerritoryGroup.boundaryTerritoryGroupId, boundary.id);
    } : undefined,
    onRemoveBoundaryClick: canRemoveBoundary ? () => {
      removeCustomBoundary(boundary.id);
    } : undefined,
    onManuallyEditPolygonClick: canEditBoundary ? () => {
      startTerritoryEdit(boundary.id);
    } : undefined,
    onBoundarySettingsClick: isEditModeDisabled ? undefined : () => {
      editBoundarySettings({
        boundaryGroupId: boundaryTerritoryGroup.boundaryGroupId,
        boundaryTerritoryGroupId: boundaryTerritoryGroup.boundaryTerritoryGroupId,
        boundaryId: boundary.id,
      });
    },
    onExportDataInBoundaryClick: isBoundaryExportLocationsAllowed && !isEmpty ?
      () => exportBoundaryData(boundaryTerritoryGroup.boundaryTerritoryGroupId, boundary.id) : undefined,
    onExportContainedBoundaries: isMapPresentational ? undefined : () => openExportContainedBoundaries({
      filter: {
        type: 'boundary',
        boundaryId: boundary.id,
        boundaryGroupId: boundaryTerritoryGroup.boundaryGroupId,
        matchings: boundaryTerritoryGroup.matchings,
      },
    }),
    onZoomToBoundaryClick: () => zoomToBoundary([{
      boundaryGroupId: boundaryTerritoryGroup.boundaryGroupId,
      boundaryId: boundary.id,
    }]),
  };

  return (
    <BoundaryItemComponent
      legendMode={legendMode}
      boundaryGroupId={boundaryTerritoryGroup.boundaryGroupId}
      setting={setting}
    />
  );
};
